import React, { Fragment, useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { toast } from "sonner";
import { renameDocumentFolder } from "services/documentFolderService";
import { ConfirmModal } from "components/shared/ConfirmModal";

/**
 * Modal to rename an existing folder
 */
export function RenameFolderModal({ isOpen, onClose, onRenameSuccess, folder }) {
  const [folderName, setFolderName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmRename, setShowConfirmRename] = useState(false);

  useEffect(() => {
    if (isOpen && folder) {
      setFolderName(folder.Name || "");
      setShowConfirmRename(false);
    }
  }, [isOpen, folder]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!folderName.trim()) {
      toast.error("Please enter a valid folder name");
      return;
    }

    if (folderName.trim() === folder?.Name) {
      onClose();
      return;
    }

    setShowConfirmRename(true);
  };

  const handleConfirmRename = async () => {
    setIsSubmitting(true);
    try {
      const res = await renameDocumentFolder({
        folderId: folder.FolderId,
        name: folderName.trim(),
      });

      if (res.success) {
        toast.success(res.message || "Folder renamed successfully");
        setShowConfirmRename(false);
        onClose();
        if (onRenameSuccess) onRenameSuccess(folder.FolderId, folderName.trim());
      } else {
        toast.error(res.error || "Failed to rename folder");
      }
    } catch (err) {
      toast.error(err.message || "Error renaming folder");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Transition appear show={Boolean(isOpen)} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[300] flex flex-col items-center justify-center p-4 sm:px-5"
          onClose={onClose}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-md origin-top flex-col overflow-hidden rounded-lg bg-white shadow-xl transition-all">
              <div className="dark:bg-dark-800 flex items-center justify-between bg-gray-100 px-4 py-3 sm:px-5 border-b border-gray-200 dark:border-dark-600">
                <DialogTitle
                  as="h3"
                  className="dark:text-dark-100 flex items-center gap-2 text-base font-semibold text-gray-900"
                >
                  <PencilIcon className="h-5 w-5 text-primary-500" />
                  Rename Folder
                </DialogTitle>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full"
                  disabled={isSubmitting}
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
                <div>
                  <label
                    htmlFor="rename-folder-name"
                    className="block text-xs font-medium text-gray-700 dark:text-dark-200 mb-1"
                  >
                    Folder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="rename-folder-name"
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    maxLength={50}
                    autoFocus
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-50 dark:placeholder-dark-400"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outlined"
                    className="min-w-[5.5rem] rounded-full"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    className="min-w-[5.5rem] rounded-full"
                    disabled={isSubmitting || !folderName.trim()}
                  >
                    Save
                  </Button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* Confirmation modal for folder rename */}
      <ConfirmModal
        show={showConfirmRename}
        onClose={() => setShowConfirmRename(false)}
        onOk={handleConfirmRename}
        confirmLoading={isSubmitting}
        state="pending"
        buttonColor="primary"
        messages={{
          pending: {
            title: "Rename Folder",
            description: `Are you sure you want to rename "${folder?.Name}" to "${folderName.trim()}"?`,
            actionText: "Rename",
          },
          success: {
            title: "Folder Renamed",
            description: "The folder has been successfully renamed.",
            actionText: "Done",
          },
          error: {
            title: "Rename Failed",
            description: "Failed to rename the folder. Please try again.",
            actionText: "Retry",
          },
        }}
      />
    </>
  );
}

export default RenameFolderModal;
